import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Wallet, UserPlus, Eye, EyeOff } from 'lucide-react';
import { userApi } from '../services/api';
import Alert from '../components/Alert';
import { EnvironmentBanner } from '../components/EnvironmentBanner';

interface ValidationMessageItem {
    message?: string;
    path?: string[];
}

const getRegisterErrorMessage = (error: any): string => {
    const responseData = error?.response?.data;
    const translatedMessage = responseData?.translatedMessage;

    if (typeof translatedMessage === 'string' && translatedMessage.trim()) {
        return translatedMessage;
    }

    const apiMessage = responseData?.message;

    if (typeof apiMessage === 'string' && apiMessage.trim()) {
        return apiMessage;
    }

    if (Array.isArray(apiMessage)) {
        const messages = apiMessage
            .map((item: ValidationMessageItem) => {
                if (!item || typeof item !== 'object') return null;
                const field = Array.isArray(item.path) && item.path.length > 0 ? `${item.path.join('.')}: ` : '';
                return typeof item.message === 'string' && item.message.trim()
                    ? `${field}${item.message}`
                    : null;
            })
            .filter((message): message is string => Boolean(message));

        if (messages.length > 0) {
            return messages.join(' ');
        }
    }

    return 'Falha ao criar o usuário.';
};

const Register: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const [name, setName] = useState<string>('');
    const [username, setUsername] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<boolean>(false);
    const [logoSrc, setLogoSrc] = useState<string | null>('/logo.svg');

    useEffect(() => {
        if (!token) {
            setError('Token de convite inválido ou ausente.');
        }
    }, [token]);

    const handleLogoError = () => {
        if (logoSrc === '/logo.svg') setLogoSrc('/logo.png');
        else setLogoSrc(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token) return;

        setError(null);

        if (!name.trim()) {
            setError('O nome é obrigatório.');
            return;
        }

        if (!username.trim()) {
            setError('O usuário é obrigatório.');
            return;
        }

        if (!password || password.length < 8) {
            setError('A senha deve ter pelo menos 8 caracteres.');
            return;
        }

        try {
            setSubmitting(true);
            await userApi.register(token, {
                name: name.trim(),
                username: username.trim(),
                password: password,
            });
            setSuccess(true);
            // Redirect to login immediately
            navigate('/login');
        } catch (e: any) {
            setError(getRegisterErrorMessage(e));
        } finally {
            setSubmitting(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-50 to-secondary-50">
                <EnvironmentBanner />
                <div className="flex-1 flex items-center justify-center p-6">
                    <div className="w-full max-w-md bg-white rounded-xl shadow-sm border border-secondary-200 p-8">
                        <Alert type="error" title="Token inválido" message="O link de convite é inválido ou expirou." />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-50 to-secondary-50">
            <EnvironmentBanner />
            <div className="flex-1 flex items-center justify-center p-6">
                <div className="w-full max-w-md bg-white rounded-xl shadow-sm border border-secondary-200 p-8">
                    <div className="flex items-center mb-6">
                        {logoSrc ? (
                            <img
                                src={logoSrc}
                                onError={handleLogoError}
                                alt="My Finances Logo"
                                className="h-10 w-10 mr-3 object-contain"
                            />
                        ) : (
                            <div className="h-10 w-10 mr-3 rounded-md bg-primary-600 flex items-center justify-center">
                                <Wallet className="h-6 w-6 text-white" />
                            </div>
                        )}
                        <div>
                            <h1 className="text-xl font-bold text-secondary-900 leading-tight">My Finances</h1>
                            <p className="text-sm text-secondary-600">Crie sua conta para começar</p>
                        </div>
                    </div>

                    {!success && (
                        <>
                            <div className="flex items-center gap-2 mb-6">
                                <UserPlus className="h-5 w-5 text-primary-600" />
                                <h2 className="text-lg font-semibold text-secondary-900">Criar Conta</h2>
                            </div>

                            {error && <Alert type="error" title="Erro ao criar conta" message={error} />}

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                                        Nome Completo *
                                    </label>
                                    <input
                                        className="input-field w-full"
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="Digite seu nome completo"
                                        required
                                        autoFocus
                                        disabled={submitting}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                                        Usuário *
                                    </label>
                                    <input
                                        className="input-field w-full"
                                        type="text"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        placeholder="Digite seu usuário"
                                        required
                                        disabled={submitting}
                                    />
                                    <p className="mt-1 text-xs text-secondary-500">
                                        Este será seu nome de usuário para fazer login
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                                        Senha *
                                    </label>
                                    <div className="relative">
                                        <input
                                            className="input-field w-full pr-10"
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Digite sua senha"
                                            required
                                            minLength={8}
                                            disabled={submitting}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                                            disabled={submitting}
                                        >
                                            {showPassword ? (
                                                <EyeOff className="h-5 w-5" />
                                            ) : (
                                                <Eye className="h-5 w-5" />
                                            )}
                                        </button>
                                    </div>
                                    <p className="mt-1 text-xs text-secondary-500">
                                        Mínimo de 8 caracteres
                                    </p>
                                </div>

                                <button
                                    type="submit"
                                    className="btn-primary w-full disabled:opacity-50 flex items-center justify-center gap-2"
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            Criando conta...
                                        </>
                                    ) : (
                                        <>
                                            <UserPlus className="w-4 h-4" />
                                            Criar Conta
                                        </>
                                    )}
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Register;
